import { memo, useEffect, useMemo, useState } from "react";
import AppsContent from "@lib/components/AppComponents/AppsContainer/AppsContent";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import AppTable from "@lib/components/MiniComponents/Table/AppTable";
import getColumns from "./columns";
import AppsHeader from "@lib/components/AppComponents/AppsContainer/AppsHeader";
import AppTableContentHeader from "@lib/components/MiniComponents/Table/AppTableContentHeader";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  PaginationType,
  TableHeaderFilter,
  UseQueryProps,
} from "@lib/types/Api";
import { toast } from "react-toastify";
import { WalletAddressModal } from "@lib/components/PageComponents/Wallet";
import { type TableProps } from "antd";
import { handleTableChange } from "@lib/helpers/UrlHelper";
import { useParamsManager } from "@lib/hooks/useParamsManager";
import { useIntl } from "react-intl";
import WalletItemDetail from "./WalletItemDetail";
import { TbListDetails } from "react-icons/tb";
import { IoMdClose } from "react-icons/io";
import { AppContext } from "@lib/types";
import { ActionButton } from "@lib/components/MiniComponents/Buttons";
import { getWalletLinkNoFilter } from "../Sidebar/utils";
import { AIWallet, AIWalletAddress } from "@lib/types/Wallet";
import {
  apiAddCreditToAIWalletAddress,
  apiDeleteAIWalletAddress,
  apiGetAIWalletAddress,
  apiUpdateAIWalletAddress,
} from "@lib/api/walletAI";
import { AIPaymentModal } from "@lib/components/PageComponents/Payment";

type Props = {
  aiWalletsQuery: UseQueryProps<PaginationType<AIWallet>>;
  context: AppContext;
  detailContext?: AppContext;
  customerId?: string;
  pageIsActive?: boolean;
};

const WalletAddressList = ({
  aiWalletsQuery,
  context,
  detailContext,
  customerId,
  pageIsActive = true,
}: Props) => {
  const { walletId } = useParams();
  const { messages } = useIntl();
  const navigate = useNavigate();
  const { state } = useLocation();

  const filter = []
    .concat(walletId ? [`walletId||$eq||${walletId}`] : [])
    .concat(...(state?.filter || []))
    .concat(...(customerId ? [`customerId||$eq||${customerId}`] : []));

  const {
    params,
    setParams,
    handleRemoveFilters: handleRemoveFiltersParams,
    onPageChange,
  } = useParamsManager({
    page: 1,
    limit: 100,
    sort: "createdAt,DESC",
    join: ["customer", "wallet"],
    filter: filter,
  });

  const handleRemoveFilter = (filter: string[]) => {
    handleRemoveFiltersParams(filter);

    if (filter.includes("walletId")) {
      navigate(getWalletLinkNoFilter(context));
      return;
    }
  };

  useEffect(() => {
    setParams({
      filter: walletId ? [`walletId||$eq||${walletId}`] : [],
    });
  }, [walletId]);

  const addAIWalletAddressQuery = useQuery({
    queryFn: () => apiGetAIWalletAddress(params),
    queryKey: ["ai-wallet-address", params],
    enabled: !!params && pageIsActive,
  });
  const { data, isLoading, isFetching, refetch } = addAIWalletAddressQuery;
  const apiData = data?.data?.data;

  const addAIPaymentMutation = useMutation({
    mutationFn: ({ id, amount }: { id: string; amount: number }) =>
      apiAddCreditToAIWalletAddress(id, amount),
    onSuccess() {
      toast.success(
        (messages["common.createdMessage"] as string).replace(
          "{name}",
          messages["common.payment"] as string,
        ),
      );
      refetch();
    },
  });

  const updateAIWalletAddressMutation = useMutation({
    mutationFn: apiUpdateAIWalletAddress,
    onSuccess() {
      toast.success(
        (messages["common.updatedMessage"] as string).replace(
          "{name}",
          messages["common.wallet"] as string,
        ),
      );
      refetch();
    },
  });

  const deleteAIWalletAddressMutation = useMutation({
    mutationFn: apiDeleteAIWalletAddress,
    onSuccess() {
      toast.success(
        (messages["common.deletedMessage"] as string).replace(
          "{name}",
          messages["common.wallet"] as string,
        ),
      );
      refetch();
    },
  });

  useEffect(() => {
    if (deleteAIWalletAddressMutation.isSuccess) {
      setScanning(true);
    }
  }, [deleteAIWalletAddressMutation.isSuccess]);

  const queryClient = useQueryClient();
  const [scanning, setScanning] = useState(false);
  const [openModal, setModalOpen] = useState(false);
  const [openAIPaymentModal, setOpenAIPaymentModal] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState<AIWalletAddress>();

  useEffect(() => {
    if (scanning) {
      toast.promise(
        new Promise((resolve) => {
          setTimeout(() => {
            queryClient.invalidateQueries({
              queryKey: ["ai-wallet-address"],
            });
            queryClient.removeQueries({
              queryKey: ["ai-wallet-address"],
            });
            setScanning(false);
            return resolve(true);
          }, 5000);
        }),
        {
          pending: messages["scanRequest.pending"] as string,
          success: messages["scanRequest.success"] as string,
          error: messages["scanRequest.error"] as string,
        },
      );
    }
  }, [scanning]);

  useEffect(() => {
    if (!openModal && !openAIPaymentModal) setSelectedAddress(null);
  }, [openModal, openAIPaymentModal]);

  const toggle = (status?: boolean) => {
    if (typeof status !== "undefined") setModalOpen(status);
    else setModalOpen(!openModal);
  };
  const toggleAIPaymentModal = (status?: boolean) => {
    if (typeof status !== "undefined") setOpenAIPaymentModal(status);
    else setOpenAIPaymentModal(!openAIPaymentModal);
  };

  const handleSelectAddress = (walletAddress: AIWalletAddress) => {
    setSelectedAddress(walletAddress);
    toggle(true);
  };
  const handleSelectAIPaymentAddress = (walletAddress: AIWalletAddress) => {
    setSelectedAddress(walletAddress);
    toggleAIPaymentModal(true);
  };

  const onChange: TableProps<AIWalletAddress>["onChange"] = (
    pagination,
    filters,
    sorter,
  ) => {
    handleTableChange({ pagination, filters, sorter, params, setParams });
  };

  const columns = useMemo(
    () =>
      getColumns({
        context,
        detailContext,
        apiData,
        deleteQuery: deleteAIWalletAddressMutation,
        openModal,
        handleSelectAddress,
        handleSelectAIPaymentAddress,
        params,
        messages,
        navigate,
      }),
    [
      apiData,
      deleteAIWalletAddressMutation,
      openModal,
      handleSelectAIPaymentAddress,
      handleSelectAddress,
      params,
    ],
  );

  const hiddenFilters = useMemo(() => {
    if (!!detailContext) {
      return ["walletId", "customerId"];
    }
    if (context === AppContext.CUSTOMER) {
      return ["walletId", "customerId"];
    }
    return [];
  }, [context, detailContext]);

  const filters = useMemo(() => {
    if (!params?.filter) return [];

    return params?.filter
      .map((f) => ({
        key: f.split("||")[0],
        title: columns.find((c) => c?.key === f.split("||")[0])?.title || "",
        operator: f.split("||")[1],
        value: f.split("||")[2],
      }))
      .filter((f) => !hiddenFilters.includes(f.key)) as TableHeaderFilter[];
  }, [params, columns]);

  return (
    <>
      <AppsHeader>
        <AppTableContentHeader
          showInfo
          page={params?.page || 1}
          perPage={params?.limit || 100}
          onPageChange={onPageChange}
          query={addAIWalletAddressQuery}
          filters={filters}
          handleRemoveFilters={handleRemoveFilter}
          plusProps={
            context === AppContext.CUSTOMER_LEAD && detailContext
              ? undefined
              : {
                  onClick: () => toggle(true),
                  disabled: isLoading,
                  loading: isLoading,
                }
          }
        />
      </AppsHeader>
      <AppsContent>
        <AppTable
          dataSource={apiData}
          columns={columns}
          onChange={onChange}
          rowKey={"id"}
          loading={isLoading || isFetching || scanning}
          minusHeight={20}
          expandable={{
            // showExpandColumn: false,
            columnTitle: "#",
            columnWidth: "50px",
            fixed: "right",
            expandIcon: ({ expanded, record, onExpand }) => (
              <ActionButton
                type={expanded ? "close" : "detail"}
                icon={expanded ? <IoMdClose /> : <TbListDetails />}
                color={expanded ? "red" : "green"}
                onClick={(e) => onExpand(record, e)}
                noLabel
              />
            ),
            expandedRowRender: (record) => <WalletItemDetail item={record} />,
            onExpand(expanded, record) {
              try {
                const find = document.querySelector(
                  `[data-row-key="${record.id}"]`,
                );
                if (find) {
                  find.setAttribute(
                    "data-row-expanded",
                    expanded ? "true" : "false",
                  );
                }
              } catch (error) {}
            },
          }}
        />

        <AIPaymentModal
          openModal={openAIPaymentModal}
          toggleModal={toggleAIPaymentModal}
          addMutationQuery={addAIPaymentMutation}
          updateMutationQuery={updateAIWalletAddressMutation}
          walletAddress={selectedAddress}
          customerId={customerId || selectedAddress?.customerId}
        />

        <WalletAddressModal.AIWalletAddressModal
          walletsQuery={aiWalletsQuery}
          openModal={openModal}
          toggleModal={toggle}
          walletAddress={selectedAddress}
          customerId={customerId}
        />
      </AppsContent>
    </>
  );
};

export default memo(WalletAddressList);
